import { Camera } from '@babylonjs/core/Cameras/camera';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3, Color4, Vector3 } from '@babylonjs/core/Maths';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Scene } from '@babylonjs/core/scene';
import { eAxis } from '@models/classes/enums';
import { KbVector } from '@models/classes/primitives/kb-vector';
import { SceneNode } from '@models/classes/scene-node';
import { Highlighter } from '@view/modules/highlighter';
import { getCameraPositionWithOffset } from './math-helper';

export interface ITextLabelStyle {
    font?: string;
}
export interface ITextLabel {
    id?: string;
    text: string;
    position: KbVector;
    style?: ITextLabelStyle;
    color: Color4;
}

export class GuiHelper {
    private textLabelMap = new Map<string, Mesh>();
    private gridSettings: {
        gridMesh?: TransformNode;
        gridAxesMesh?: TransformNode;
    } = {};

    constructor(private scene: Scene, private highlighter: Highlighter) {
        const cameraPosition = new Vector3();
        scene.registerBeforeRender(() => {
            if (scene.activeCamera && this.textLabelMap.size > 0) {
                this.textLabelMap.forEach(mesh => {
                    const camera = scene.activeCamera!;
                    cameraPosition.copyFrom(getCameraPositionWithOffset(camera));
                    mesh.lookAt(cameraPosition);
                    if (camera.mode !== Camera.ORTHOGRAPHIC_CAMERA) {
                        const size = cameraPosition.subtractInPlace(mesh.position).length() * 0.05;
                        mesh.scaling = mesh.scaling.copyFromFloats(size, size, size);
                    }
                });
            }
        });
    }

    enableGrid(sceneNode: SceneNode, skipOrigin = false) {
        if (this.gridSettings.gridMesh) {
            return;
        }
        const bgColor = sceneNode?.backgroundColor;
        let bgColor3: Color3;
        if (!bgColor) {
            bgColor3 = new Color3(this.scene.clearColor.r, this.scene.clearColor.g, this.scene.clearColor.b);
        } else {
            bgColor3 = bgColor.a === 0 ? Color3.Black() : new Color3(bgColor.r, bgColor.g, bgColor.b);
        }
        const gridStepSize = sceneNode.gridStepSize || 1;
        const gridSize = sceneNode.gridSize || 10;
        const gridNormal = sceneNode.gridNormal;
        const gridMajorInterval = sceneNode.gridMajorInterval || 0;

        const strongLineColor = Color4.FromColor3(bgColor3.toLuminance() > 0.5 ? Color3.Black() : Color3.White());
        const lightLineColor = strongLineColor.scale(0.4).add(Color4.FromColor3(bgColor3.scale(0.6)));

        const points = this.calculateGridPoints(gridStepSize, gridSize, gridNormal);
        const pointColors: Color4[][] = [];
        const linesPerSide = points.length / 2;
        for (let s = 0; s < 2; s++) {
            for (let i = 0; i < linesPerSide; i++) {
                if (gridMajorInterval === 0) {
                    pointColors.push([lightLineColor, lightLineColor]);
                } else if (Math.ceil(i / 2) % gridMajorInterval === 0) {
                    pointColors.push([strongLineColor, strongLineColor]);
                } else {
                    pointColors.push([lightLineColor, lightLineColor]);
                }
            }
        }

        if (skipOrigin) {
            points.splice(linesPerSide, 1);
            points.splice(0, 1);
            pointColors.splice(linesPerSide, 1);
            pointColors.splice(0, 1);
        }

        const gridMesh = MeshBuilder.CreateLineSystem(
            'grid',
            {
                lines: points,
                colors: pointColors,
                updatable: false,
            },
            this.scene
        );

        // gridMesh.color = lineColor.scale(0.4).add(bgColor3.scale(0.6));
        gridMesh.isPickable = false;

        this.gridSettings.gridMesh = gridMesh;
    }

    disableGrid() {
        if (this.gridSettings.gridMesh) {
            this.gridSettings.gridMesh.dispose();
            delete this.gridSettings.gridMesh;
        }
    }

    enableGridAxes(sceneNode: SceneNode) {
        if (this.gridSettings.gridAxesMesh) {
            return;
        }
        // const bgColor = sceneNode.backgroundColor;
        // let bgColor3: Color3;
        // if (!bgColor) {
        //     bgColor3 = new Color3(this.scene.clearColor.r, this.scene.clearColor.g, this.scene.clearColor.b);
        // } else {
        //     bgColor3 = bgColor.a === 0 ? Color3.Black() : new Color3(bgColor.r, bgColor.g, bgColor.b);
        // }

        // const lineColor = bgColor3.toLuminance() > 0.5 ? Color3.Black() : Color3.White();
        const gridStepSize = sceneNode.gridStepSize || 1;
        const gridSize = (sceneNode.gridSize || 10) * gridStepSize;

        const colors = [
            Color4.FromColor3(Color3.Red()),
            Color4.FromColor3(Color3.Green()),
            Color4.FromColor3(Color3.Blue()),
        ];
        const pointsAxes = [
            [new Vector3(-gridSize, 0, 0), new Vector3(gridSize, 0, 0)],
            [new Vector3(0, -gridSize, 0), new Vector3(0, gridSize, 0)],
            [new Vector3(0, 0, -gridSize), new Vector3(0, 0, gridSize)],
        ];

        const pointColors = [
            [colors[0], colors[0]],
            [colors[1], colors[1]],
            [colors[2], colors[2]],
        ];

        const gridMeshAxes = MeshBuilder.CreateLineSystem(
            'gridAxes',
            {
                lines: pointsAxes,
                updatable: false,
                colors: pointColors,
            },
            this.scene
        );

        gridMeshAxes.isPickable = false;

        this.gridSettings.gridAxesMesh = gridMeshAxes;

        this.createTextLabels([
            { text: 'x', position: new KbVector(gridSize, 0.5, 0), color: colors[0] },
            { text: 'y', position: new KbVector(0.5, gridSize, 0), color: colors[1] },
            { text: 'z', position: new KbVector(0, 0.5, gridSize), color: colors[2] },
        ]);
    }

    disableGridAxes() {
        if (this.gridSettings.gridAxesMesh) {
            this.gridSettings.gridAxesMesh.dispose();
            delete this.gridSettings.gridAxesMesh;
        }
    }

    private createTextLabels(items: ITextLabel[], style: ITextLabelStyle = {}) {
        if (!style.font) {
            style.font = 'bold 32px sans-serif';
        }

        items.forEach(label => {
            const font = label.style && label.style.font ? label.style.font : style.font!;

            if (!label.id) {
                label.id = label.text;
            }

            if (this.textLabelMap.has(label.id)) {
                const labelMesh = this.textLabelMap.get(label.id)!;
                labelMesh.position = label.position.toVec3();
            } else {
                // Set height for plane
                const fontSize = parseInt(font.replace(/\D+(\d+)px.*/, '$1')) || 32;
                const planeHeight = fontSize / 32;
                const textureHeight = 2 * fontSize;
                const ratio = planeHeight / textureHeight;

                // Use a temporay canvas to calculate the length of the text
                const temp = document.createElement('canvas');
                temp.width = 64;
                temp.height = 64;
                const tmpctx = temp.getContext('2d');
                if (tmpctx) {
                    tmpctx.font = font;
                    const textureWidth = tmpctx.measureText(label.text).width + 8;
                    temp.width = 0;
                    temp.height = 0;

                    // Create dynamic texture and write the text
                    const dynamicId = `axisLabel${label.id}`;
                    const planeWidth = textureWidth * ratio;
                    const dynamicTexture = new DynamicTexture(
                        dynamicId + '-labelTexture',
                        { width: textureWidth, height: textureHeight },
                        this.scene,
                        false
                    );
                    const mat = new StandardMaterial(dynamicId + '-labelMaterial', this.scene);
                    mat.emissiveTexture = dynamicTexture;
                    mat.diffuseTexture = dynamicTexture;
                    mat.sideOrientation = Mesh.BACKSIDE;
                    dynamicTexture.hasAlpha = true;
                    dynamicTexture.drawText(
                        label.text,
                        null,
                        null,
                        font,
                        label.color.toHexString(),
                        'rgba(0,0,0,0)',
                        true
                    );

                    // Create plane and set dynamic texture as material
                    const plane = MeshBuilder.CreatePlane(
                        dynamicId,
                        { width: planeWidth, height: planeHeight },
                        this.scene
                    );
                    plane.material = mat;
                    plane.position = label.position.toVec3();
                    plane.isPickable = false;

                    this.textLabelMap.set(label.id, plane);
                }
            }
        });
    }

    private calculateGridPoints(stepSize: number, gridCount: number, normal: eAxis, gridFullSize?: number) {
        const points: Vector3[][] = [];
        const wV1 = new Vector3(),
            wV2 = new Vector3(),
            vStep = new Vector3();

        if (gridFullSize === undefined) {
            gridFullSize = gridCount;
        }

        let uVec: Vector3;
        let vVec: Vector3;
        switch (normal) {
            case eAxis.x:
                uVec = new Vector3(0, stepSize, 0);
                vVec = new Vector3(0, 0, stepSize);
                break;
            case eAxis.y:
                uVec = new Vector3(stepSize, 0, 0);
                vVec = new Vector3(0, 0, stepSize);
                break;
            case eAxis.z:
                uVec = new Vector3(stepSize, 0, 0);
                vVec = new Vector3(0, stepSize, 0);
                break;
        }

        wV1.setAll(0).addInPlace(vVec).scaleInPlace(-1).scaleInPlace(gridFullSize);
        wV2.setAll(0).scaleInPlace(-1).addInPlace(vVec).scaleInPlace(gridFullSize);
        for (let i = 0; i < gridCount * 2 + 1; i++) {
            const d = (2 * (i % 2) - 1) * Math.ceil(i / 2);
            vStep.copyFrom(uVec).scaleInPlace(d);
            points.push([wV1.add(vStep), wV2.add(vStep)]);
        }
        wV1.setAll(0).addInPlace(uVec).scaleInPlace(-1).scaleInPlace(gridFullSize);
        wV2.setAll(0).scaleInPlace(-1).addInPlace(uVec).scaleInPlace(gridFullSize);
        for (let i = 0; i < gridCount * 2 + 1; i++) {
            const d = (2 * (i % 2) - 1) * Math.ceil(i / 2);

            vStep.copyFrom(vVec).scaleInPlace(d);
            points.push([wV1.add(vStep), wV2.add(vStep)]);
        }

        return points;
    }
}
